import { useContext, useEffect, useState } from 'react';
import { Element } from 'react-scroll';
import cx from 'classnames';
import {
  Heading,
  Pill,
  Icon,
  CopyLink,
  enums,
  utils as u,
  Button,
} from 'edrolo-ui';
import {
  useExamLock,
  useUserCourseProgress,
} from 'edrolo/domains/bySchoolClass';
import { useAnalytics } from 'edrolo/analytics';
import { Exam } from '../../../../../../types/Assemblage';
import urlResolver from 'edrolo/utils/URLResolver';
import { ExamLockHook } from './types';
import ExamEntryModal from './components/ExamEntryModal';
import Utils from 'edrolo/components/exams/utils';
import { ClassContext, UserContext } from 'edrolo/context';
import styles from './styles.scss';
import { SubmissionsProgress, StudentProgress } from './components';
import { ExamSitting } from '../../types/ExamSitting';
import { WaffleFlag } from 'edrolo/utils/Waffle';

export interface ExamCardProps {
  exam: Exam;
  topicId?: number;
  insightsAvailabilityFlagName: string;
}

export const ExamCard = ({
  exam,
  topicId,
  insightsAvailabilityFlagName,
}: ExamCardProps) => {
  const [showModal, setShowModal] = useState(false);

  const {
    data: { classId, isTeacherView, assemblage },
  } = useContext(ClassContext);

  // Formative migration (behind the `formative-assessments` waffle flag): when the
  // flag is on for a formative exam, every ExamCard interaction below bypasses the
  // legacy modal / Exam Master flow and hard-redirects to the unified assessments
  // SPA (teacher vs student route, RFC 086 / LLSB-1002). The unified route no
  // longer needs courseId; the guard is kept for behaviour parity with the
  // pre-unified flow. Remove this short-circuit when the flag is retired.
  const courseId = assemblage?.course?.id;
  const redirectToFormative =
    !!global.waffle?.flag_is_active('formative-assessments') &&
    exam.type === 'formative' &&
    courseId != null;
  const formativeUrl = isTeacherView
    ? `/home/assessments/${exam.id}`
    : `/student/assessments/${exam.id}`;

  const { profile } = useContext(UserContext);

  const { isLocked, setIsLocked }: ExamLockHook = useExamLock(classId, exam.id);

  // TODO lift this into the course context, it is used in multiple places and it would
  // be useful/less error prone managing the loading state in one place.
  const {
    data: userProgressData,
    error: userProgressError,
    loading: userProgressLoading,
  } = useUserCourseProgress(profile?.user_id, classId);
  const examSitting: ExamSitting =
    !userProgressError &&
    !userProgressLoading &&
    userProgressData?.exams[exam.id];

  const { track: analyticsTrack } = useAnalytics({});
  const track = (eventName: string) =>
    analyticsTrack(eventName, { exam_id: exam.id });

  const isExamAvailable = isTeacherView || !isLocked;
  const isExamSubmitted = examSitting?.submitted_ts;

  // If we've come to the page with a '#exam-{id}' url fragment,
  // and the user has an existing sitting for this exam,
  // go straight to the exam master page.
  const shouldAutoOpen =
    isExamAvailable &&
    global.document.location.hash.startsWith(`#exam-${exam.id}`);

  // If we've come to the page with a '#exam-{id}' url fragment,
  // (eg via a share link, or being bounced out of the EM page for
  // not having a sitting), open the "Start Exam" modal.
  const shouldAutoRedirectToExam =
    isExamAvailable && shouldAutoOpen && examSitting?.id;

  // Printed exams have a shortlink like "edrolo.com.au/e6786" which will
  // eventually bring the student here with a "#exam-{id}-transcribe" fragment
  const cameFromShortLink: boolean =
    document.location.hash === `#exam-${exam.id}-transcribe`;

  // TODO document what this is for
  const isOfflineModeFromCookie =
    Utils.getExamModeCookie(exam.id) === Utils.EXAM_MODE.OFFLINE;

  // If the user has an active sitting for the exam (ie: they have
  // started or completed the exam) retrieve the appropriate URLResolver
  // based on the state of the sitting.
  const getExistingSittingUrl = () => {
    let routeName;
    if (isExamSubmitted) {
      routeName = 'exams-section-solution';
    } else if (cameFromShortLink || isOfflineModeFromCookie) {
      routeName = 'exams-transcribe';
    } else {
      routeName = 'exams-section';
    }

    return urlResolver.reverse(routeName, {
      examId: exam.id,
      sectionId: exam.first_section_id,
    });
  };
  const existingSittingUrl = getExistingSittingUrl();

  // On arriving with a '#exam-{id}' fragment, auto-act on the exam: a formative
  // exam (flag on) redirects to the formative SPA; otherwise resume an existing
  // sitting or open the entry modal, as appropriate.
  useEffect(() => {
    if (!isExamAvailable) {
      return;
    }
    if (redirectToFormative) {
      if (shouldAutoRedirectToExam || shouldAutoOpen) {
        window.location = formativeUrl as unknown as Location;
      }
      return;
    }
    if (shouldAutoRedirectToExam) {
      global.window.location = existingSittingUrl;
    } else if (shouldAutoOpen && !showModal) {
      setShowModal(true);
    }
  }, [
    isExamAvailable,
    redirectToFormative,
    formativeUrl,
    shouldAutoRedirectToExam,
    shouldAutoOpen,
    existingSittingUrl,
    showModal,
  ]);

  const shortLinkProps = {
    id: exam.id,
    appLabel: 'exams',
    model: 'exam',
    queryString: '?',
  };

  const viewSubmissionsUrl = topicId
    ? urlResolver.reverse('topic-exam-grid', {
        examId: exam.id,
        classId,
        topicId,
      })
    : urlResolver.reverse('course-exam-grid', {
        examId: exam.id,
        classId,
      });

  const handleExamCard = () => {
    track('exam card clicked');
    if (redirectToFormative) {
      window.location = formativeUrl as unknown as Location;
      return;
    }
    if (isExamSubmitted) {
      window.location = existingSittingUrl;
    } else {
      setShowModal(true);
    }
  };

  const handleExamStart = () => {
    track('exam entry modal start exam clicked');
    if (redirectToFormative) {
      window.location = formativeUrl as unknown as Location;
      return;
    }
    const url = urlResolver.reverse('exams', {
      examId: exam.id,
    });
    window.location = url;
  };

  const handleExamStartOffline = () => {
    track('exam entry modal get my results clicked');
    if (redirectToFormative) {
      window.location = formativeUrl as unknown as Location;
      return;
    }
    const url = urlResolver.reverse('exams-transcribe', {
      examId: exam.id,
      sectionId: exam.first_section_id,
    });
    window.location = url;
  };

  return (
    <Element
      name={`${exam.id}`}
      id={`${exam.id}`}
      className={cx(styles.examCard, {
        [styles.highlight]: !exam.is_published,
      })}
      data-appcues="exam-card"
      data-test="exam-card"
    >
      <div className={styles.titleBlock}>
        {(isTeacherView || isLocked) && (
          <button
            disabled={!isTeacherView}
            onClick={() => {
              setIsLocked(!isLocked);
              track('exam lock toggled');
            }}
            data-test="lock-exam-button"
          >
            <Icon
              color={enums.ColorTheme.SECONDARY}
              name={isLocked ? enums.Icons.LOCK_CLOSED : enums.Icons.LOCK_OPEN}
            />
          </button>
        )}

        <button
          className={styles.link}
          disabled={!isExamAvailable}
          onClick={handleExamCard}
        >
          <Pill variant={enums.PillVariant.FADE} color={enums.ColorHue.TEAL}>
            <Icon name={enums.Icons.PENCIL_CHECK} className={u.mr1} />
            Assessment
          </Pill>
        </button>

        <button
          className={styles.link}
          disabled={!isExamAvailable}
          onClick={handleExamCard}
        >
          <Heading level={4} className={styles.title}>
            {exam.title}
          </Heading>
        </button>

        {examSitting && examSitting.marks_final && (
          <Pill
            variant={enums.PillVariant.OUTLINE}
            color={enums.ColorTheme.FOREGROUND}
          >
            {`${+examSitting.marks_final}/${Math.round(
              +exam.marks_total
            )} Marks`}
          </Pill>
        )}

        {!exam.is_published && (
          <Pill color={enums.ColorHue.GREY}>Unpublished</Pill>
        )}
      </div>

      {isTeacherView ? (
        <div className={cx(u.flex, u.gap2)}>
          <WaffleFlag name={insightsAvailabilityFlagName}>
            <Button.Anchor
              size={enums.Size.SMALL}
              variant={enums.Variant.FADE}
              color={enums.ColorTheme.SECONDARY}
              href={urlResolver.reverse('em-insights-page', {
                classId,
                topicId,
                examId: exam.id,
              })}
            >
              <Icon name={enums.Icons.MAGIC} className={u.mr1} />
              Insights
            </Button.Anchor>
          </WaffleFlag>

          {redirectToFormative ? (
            <Button.Anchor
              size={enums.Size.SMALL}
              variant={enums.Variant.GHOST}
              href={`/home/assessments/${exam.id}/results/${classId}`}
            >
              <SubmissionsProgress exam={exam} data-test="view-submissions" />
              View submissions
            </Button.Anchor>
          ) : (
            <Button.Link
              size={enums.Size.SMALL}
              variant={enums.Variant.GHOST}
              to={viewSubmissionsUrl}
            >
              <SubmissionsProgress exam={exam} data-test="view-submissions" />
              View submissions
            </Button.Link>
          )}

          <CopyLink
            buttonProps={{
              size: enums.Size.SMALL,
              variant: enums.Variant.OUTLINE,
            }}
            shortLinkProps={shortLinkProps}
          />
        </div>
      ) : (
        <StudentProgress examSitting={examSitting} />
      )}

      {showModal && (
        <ExamEntryModal
          examId={exam.id}
          examTitle={exam.title}
          onClose={() => {
            if (global.document.location.hash.startsWith('#exam')) {
              global.document.location.hash = '';
            }
            setShowModal(false);
          }}
          hasSitting={!!examSitting?.id}
          offlineMode={
            cameFromShortLink || (isExamSubmitted && isOfflineModeFromCookie)
          }
          handleExamStart={handleExamStart}
          handleExamStartOffline={handleExamStartOffline}
        />
      )}
    </Element>
  );
};
